/**
 * 청구 영수증 인쇄 유틸.
 *
 * 새 창을 열어서 styled-components 없이 raw HTML/CSS로 영수증을 그린다.
 * 정책 트리(detail.pricingPolicyTree)를 순회하며 그룹별 섹션 + 정책 트리 정산 표를 동적 생성.
 *
 * 사용처: BillingPanel의 "영수증 인쇄하기" 버튼 onClick.
 */

import {
    parseTierDiscountInfo,
    computeItemUsage,
    computeGroupTotal,
    findGroupCompensation
} from './billingPolicyTree';
import { ServiceItemNumber } from './serviceItem';
import { getRoundingModeLabel } from 'components/common/SunnyUtils';

/**
 * 항목번호별 사용량 라벨 — 인쇄용 HTML 문자열.
 * 화면의 UsageDisplay와 동일한 배지 스타일(C안)을 HTML로 시뮬레이션.
 */
function getItemUsageLabel(item, detail) {
    const itemNumber = item?.serviceItemNumber;
    const roundingMode = item?.roundingMode || 'ceil';

    let rawValue = detail?.rawItemCounts?.[String(itemNumber)];
    const roundedValue = detail?.roundedItemCounts?.[String(itemNumber)];
    if (rawValue == null) {
        if (itemNumber === ServiceItemNumber.SDK_AGENT) {
            rawValue = detail?.avgAgentCount;
        } else if (itemNumber === ServiceItemNumber.POLICY_SERVER) {
            rawValue = detail?.policyServerCnt;
        }
    }
    if (rawValue == null) return '사용량 정보 없음';
    const raw = parseFloat(rawValue);
    if (isNaN(raw) || raw <= 0) return '사용량 정보 없음';
    const isDecimal = raw % 1 !== 0;
    const rounded = roundedValue != null && !isNaN(parseFloat(roundedValue)) ? parseFloat(roundedValue) : (isDecimal ? null : raw);

    if (!isDecimal || rounded == null) {
        return `${raw}대`;
    }
    const label = getRoundingModeLabel(roundingMode);
    const grayBadge = `<span style="display:inline-block;padding:2px 8px;background:#f1f5f9;border:1px solid #cbd5e1;border-radius:6px;font-size:0.75rem;font-weight:600;color:#475569;margin-left:6px">실측 ${raw}</span>`;
    const blueBadge = `<span style="display:inline-block;padding:2px 8px;background:#dbeafe;border:1px solid #93c5fd;border-radius:6px;font-size:0.75rem;font-weight:600;color:#1e40af;margin-left:6px">${label}</span>`;
    return `<strong>${rounded}대</strong>${grayBadge}${blueBadge}`;
}


/**
 * 청구 정보 → 인쇄용 새 창 열기.
 *
 * @param {Object} detail - billingDetail
 * @param {string} yearMonth - 'YYYY-MM' 또는 'YYYY-MM_final_...'
 * @param {boolean} isFinalAmount
 */
export function printBillingReceipt(detail, yearMonth, isFinalAmount) {
    if (!detail) return;

    const parts = (yearMonth || '').split('_');
    const baseYearMonth = parts[0];
    const isFinal = parts.includes('final');
    const finalizedDateStr = isFinal && parts.length > 2 ? parts[2] : null;

    const formattedMonth = baseYearMonth
        ? `${baseYearMonth.slice(0, 4)}년 ${parseInt(baseYearMonth.slice(5, 7), 10)}월`
        : '';

    let lastDay;
    if (finalizedDateStr) {
        const finalizedDate = new Date(finalizedDateStr);
        finalizedDate.setDate(finalizedDate.getDate() - 1);
        lastDay = finalizedDate.getDate();
    } else if (baseYearMonth) {
        lastDay = new Date(Number(baseYearMonth.slice(0, 4)), Number(baseYearMonth.slice(5, 7)), 0).getDate();
    } else {
        lastDay = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    }

    const policyTree = detail.pricingPolicyTree;
    const allGroups = policyTree?.serviceGroupList || [];
    // 빈 그룹 (사용량 0 또는 항목 없음) 제외
    const groups = allGroups.filter(g => {
        const items = g.servicePriceList || [];
        if (items.length === 0) return false;
        return computeGroupTotal(g, detail) > 0;
    });

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        alert('팝업이 차단되어 영수증을 인쇄할 수 없습니다. 팝업을 허용해주세요.');
        return;
    }

    printWindow.document.write('<html><head><title>SafeDB 청구 영수증</title>');
    printWindow.document.write('<style>');
    printWindow.document.write(`
        @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.6/dist/web/static/pretendard.css');
        * { box-sizing: border-box; }
        body { font-family: 'Pretendard', -apple-system, sans-serif; padding: 2rem; color: #334155; line-height: 1.5; }
        .print-header { text-align: center; margin-bottom: 2rem; padding-bottom: 1.5rem; border-bottom: 2px solid #e2e8f0; }
        .print-header h2 { margin: 0 0 4px 0; color: #1e293b; font-weight: 700; }
        .print-header p { margin: 0; color: #64748b; font-size: 0.9rem; }
        .section { margin-bottom: 2rem; }
        .section-title { font-weight: 700; color: #1e293b; font-size: 1rem; margin-bottom: 1rem; padding-left: 12px; border-left: 4px solid #3b82f6; }
        .group-title { font-weight: 700; color: #334155; font-size: 1.05rem; margin-bottom: 0.75rem; padding: 0.6rem 0.85rem; background: #f1f5f9; border-radius: 8px; }
        .info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
        .info-item { background: #f8fafc; padding: 0.75rem 1rem; border-radius: 8px; border: 1px solid #e2e8f0; }
        .info-label { color: #64748b; font-size: 0.75rem; font-weight: 600; text-transform: uppercase; }
        .info-value { color: #0f172a; font-weight: 700; font-size: 0.95rem; margin-top: 2px; }
        table { width: 100%; border-collapse: separate; border-spacing: 0; border-radius: 8px; border: 1px solid #e2e8f0; overflow: hidden; margin-bottom: 1rem; font-size: 0.85rem; }
        th { background: #f1f5f9; color: #475569; font-weight: 600; padding: 0.6rem 0.75rem; text-align: left; border-bottom: 1px solid #e2e8f0; }
        td { padding: 0.6rem 0.75rem; color: #334155; border-bottom: 1px solid #f1f5f9; }
        tbody tr:last-child td { border-bottom: none; }
        tbody tr.policy-row td { background: #fef3c7; color: #854d0e; font-weight: 700; }
        tbody tr.group-row td { background: #eff6ff; color: #1e40af; font-weight: 600; }
        tfoot th { background: #f8fafc; border-top: 2px solid #e2e8f0; border-bottom: none; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .text-green { color: #10b981; }
        .text-cyan { color: #0891b2; }
        .fw-bold { font-weight: 700; }
        .strikethrough { text-decoration: line-through; opacity: 0.55; }
        .indent-1 { padding-left: 1.5rem; }
        .indent-2 { padding-left: 3rem; }
        .badge-version { display: inline-block; background: #eef2ff; color: #4338ca; border: 1px solid #c7d2fe; padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; margin-left: 6px; }
        .summary-card { background: linear-gradient(135deg, #eff6ff, #dbeafe); border: 1px solid #bfdbfe; border-radius: 8px; padding: 1rem 1.25rem; margin-top: 1rem; }
        .summary-card .title { color: #1e40af; font-weight: 700; font-size: 0.9rem; }
        .summary-card .amount { color: #1d4ed8; font-weight: 800; font-size: 1.2rem; }
        .summary-row { display: flex; justify-content: space-between; padding: 0.35rem 0; }
        .footer { margin-top: 2rem; text-align: center; color: #94a3b8; font-size: 0.8rem; padding-top: 1rem; border-top: 1px solid #e2e8f0; }
        @media print { button { display: none; } }
    `);
    printWindow.document.write('</style></head><body>');

    // Header
    printWindow.document.write('<div class="print-header">');
    printWindow.document.write('<h2>SafeDB 청구 영수증</h2>');
    printWindow.document.write(`<p>${formattedMonth} 1일 ~ ${formattedMonth} ${lastDay}일</p>`);
    printWindow.document.write('</div>');

    // 정책 정보 (이름 + 버전 + 적용 기간만 — 정책 트리 정의는 영수증 본질이 아니므로 생략)
    printWindow.document.write('<div class="section">');
    printWindow.document.write('<div class="section-title">적용 요금 정책</div>');
    printWindow.document.write('<div class="info-grid">');
    printWindow.document.write(`<div class="info-item"><div class="info-label">정책 이름</div><div class="info-value">${detail.pricingPolicyName || '기본 정책'}${detail.pricingPolicyVersion != null ? `<span class="badge-version">v${detail.pricingPolicyVersion}</span>` : ''}</div></div>`);
    printWindow.document.write(`<div class="info-item"><div class="info-label">적용 기간</div><div class="info-value">${formattedMonth} 1일 ~ ${formattedMonth} ${lastDay}일</div></div>`);
    printWindow.document.write('</div></div>');

    // 그룹별 사용량 (사용량만, 금액 없음)
    groups.forEach(group => {
        const groupName = group.serviceItemName || '그룹';
        const groupItems = group.servicePriceList || [];

        printWindow.document.write('<div class="section">');
        printWindow.document.write(`<div class="group-title">${groupName}</div>`);

        groupItems.forEach(item => {
            const itemName = item.serviceItemName || '항목';
            const usageLabel = getItemUsageLabel(item, detail);
            const hasTiers = (item.serviceTierPriceList || []).length > 0;

            printWindow.document.write(`<div style="margin-bottom:1rem;padding:0.75rem;border:1px solid #e2e8f0;border-radius:8px">`);
            printWindow.document.write(`<div style="display:flex;justify-content:space-between;margin-bottom:0.5rem"><strong>${itemName}</strong><span style="color:#475569;font-weight:600">${usageLabel}</span></div>`);

            if (hasTiers) {
                const tiers = parseTierDiscountInfo(detail.tierDiscountInfo);
                if (tiers.length > 0) {
                    // 백엔드 응답값 우선, 없으면 프론트 합산 (하위호환)
                    const tierDiscountSum = detail?.totalTierDiscount != null
                        ? detail.totalTierDiscount
                        : tiers.reduce((s, t) => s + (t.tier_discount || 0), 0);
                    const appliedSum = detail?.totalAppliedFee != null
                        ? detail.totalAppliedFee
                        : tiers.reduce((s, t) => s + (t.monthly_fee_for_tier || 0), 0);
                    const usageSum = detail?.totalUsageFee != null
                        ? detail.totalUsageFee
                        : appliedSum + tierDiscountSum;
                    printWindow.document.write('<table>');
                    printWindow.document.write('<thead><tr><th>구간</th><th>적용 수량</th><th>사용 금액</th><th>할인</th><th class="text-right">적용 금액</th></tr></thead>');
                    printWindow.document.write('<tbody>');
                    tiers.forEach(tier => {
                        const usageFee = (tier.monthly_fee_for_tier || 0) + (tier.tier_discount || 0);
                        const discountUnit = tier.discount_type === 'percent' ? '%' : '원';
                        printWindow.document.write('<tr>');
                        printWindow.document.write(`<td class="fw-bold">${tier.agent_count_from}~${tier.agent_count_to}</td>`);
                        printWindow.document.write(`<td>${(() => { const n = parseFloat(tier.agents_in_tier); return isNaN(n) ? tier.agents_in_tier : (n % 1 === 0 ? String(n) : n.toString()); })()}</td>`);
                        printWindow.document.write(`<td>₩${usageFee.toLocaleString()}</td>`);
                        printWindow.document.write(`<td>${tier.discount_rate}${discountUnit}</td>`);
                        printWindow.document.write(`<td class="text-right fw-bold">₩${tier.monthly_fee_for_tier?.toLocaleString()}</td>`);
                        printWindow.document.write('</tr>');
                    });
                    printWindow.document.write('</tbody>');
                    printWindow.document.write(`<tfoot><tr><th colspan="4" class="text-right">총 사용 금액</th><th class="text-right">₩${usageSum.toLocaleString()}</th></tr>`);
                    printWindow.document.write(`<tr><th colspan="4" class="text-right">할인 금액</th><th class="text-right text-green">-₩${Math.abs(tierDiscountSum).toLocaleString()}</th></tr>`);
                    printWindow.document.write(`<tr><th colspan="4" class="text-right">최종 금액</th><th class="text-right" style="color:#1e40af">₩${appliedSum.toLocaleString()}</th></tr></tfoot>`);
                    printWindow.document.write('</table>');
                }
            }

            printWindow.document.write('</div>');
        });

        printWindow.document.write('</div>');
    });

    // 최종 요금 정산 표 (정책 트리 구조)
    printWindow.document.write('<div class="section">');
    printWindow.document.write('<div class="section-title">최종 요금 정산</div>');
    printWindow.document.write('<table>');
    printWindow.document.write('<thead><tr><th>요금정책 / 그룹 / 항목</th><th class="text-right">사용요금</th><th class="text-right">보전기준</th></tr></thead>');
    printWindow.document.write('<tbody>');

    const compBadgeHtml = '<span style="margin-left:8px;padding:2px 8px;border:1px solid #fef08a;border-radius:6px;background:#fefce8;font-size:0.75rem;font-weight:600;color:#ca8a04">보전적용</span>';
    function valCell(amount, strike, bold) {
        const cls = [strike ? 'strikethrough' : '', bold ? 'fw-bold' : ''].filter(Boolean).join(' ');
        return `<td class="text-right ${cls}">₩${(amount || 0).toLocaleString()}</td>`;
    }

    // 정책 행
    const policyUsage = detail.usageBasedFee != null
        ? detail.usageBasedFee
        : groups.reduce((sum, g) => sum + computeGroupTotal(g, detail), 0);
    const policyComp = detail.compensationInfo?.policy || 0;
    const policyCompensated = policyComp > 0 && policyUsage < policyComp;
    printWindow.document.write('<tr class="policy-row">');
    printWindow.document.write(`<td>${detail.pricingPolicyName || '정책'}${detail.pricingPolicyVersion != null ? `<span class="badge-version">v${detail.pricingPolicyVersion}</span>` : ''}${policyCompensated ? compBadgeHtml : ''}</td>`);
    printWindow.document.write('<td class="text-right"></td>');
    printWindow.document.write(`<td class="text-right fw-bold">${policyComp > 0 ? `₩${policyComp.toLocaleString()}` : ''}</td>`);
    printWindow.document.write('</tr>');

    // 그룹 + 항목 행들
    groups.forEach(group => {
        const groupName = group.serviceItemName || '그룹';
        const groupComp = findGroupCompensation(group, detail.compensationInfo);
        const groupCompAmount = groupComp?.amount || 0;
        const groupUsage = computeGroupTotal(group, detail);
        const groupCompensated = groupCompAmount > 0 && groupUsage < groupCompAmount;

        printWindow.document.write('<tr class="group-row">');
        printWindow.document.write(`<td class="indent-1">└ [그룹] ${groupName}${groupCompensated ? compBadgeHtml : ''}</td>`);
        printWindow.document.write('<td class="text-right"></td>');
        printWindow.document.write(`<td class="text-right">${groupCompAmount > 0 ? `₩${groupCompAmount.toLocaleString()}` : ''}</td>`);
        printWindow.document.write('</tr>');

        (group.servicePriceList || []).forEach(item => {
            const itemName = item.serviceItemName || '항목';
            const itemUsage = computeItemUsage(item.serviceItemNumber, detail);
            const itemComp = parseFloat(item.compensationAmount || 0);
            const itemUsageApplied = itemUsage >= itemComp;
            const itemCompensated = itemComp > 0 && !itemUsageApplied;
            printWindow.document.write('<tr>');
            printWindow.document.write(`<td class="indent-2">└ ${itemName}${itemCompensated ? compBadgeHtml : ''}</td>`);
            printWindow.document.write(valCell(itemUsage, !itemUsageApplied));
            printWindow.document.write(valCell(itemComp, itemUsageApplied));
            printWindow.document.write('</tr>');
        });
    });

    printWindow.document.write('</tbody></table>');
    printWindow.document.write('</div>');

    // 합계: 구간 할인 + 최종금액만 표시
    printWindow.document.write('<div class="section">');
    printWindow.document.write('<div class="section-title">합계</div>');
    if ((detail.discountFee || 0) > 0) {
        printWindow.document.write(`<div class="summary-row"><span>구간 할인</span><span class="fw-bold text-green">-₩${detail.discountFee.toLocaleString()}</span></div>`);
    }
    printWindow.document.write(`<div class="summary-card"><div class="summary-row"><span class="title">${isFinalAmount ? '최종금액 (결제금액)' : '예상금액'}</span><span class="amount">₩${(detail.finalFee || 0).toLocaleString()}</span></div></div>`);
    printWindow.document.write('</div>');

    printWindow.document.write('<div class="footer"><p>SafeDB를 이용해주셔서 감사합니다.</p></div>');
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
}
